import React, { ReactElement, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { Brands } from 'src/utilities/shared-types';
import { getConfig } from 'src/utilities/config';
import { getWindow } from 'src/utilities/window';
import { getRouteDetails } from 'src/utilities/route-utils';

export const AnalyticsScript = (): ReactElement => {
  const {
    selfServiceTealiumSrc,
    enterpriseGrubhubTealiumSrc,
    enterpriseSeamlessTealiumSrc,
  } = getConfig();

  const window = getWindow();

  const { user } = useAuth();
  const { pathname } = useLocation();
  const [isRouterReady, setIsRouterReady] = useState(false);

  useEffect(() => {
    setIsRouterReady(true);
  }, [pathname]);

  const [analyticsSourceState, setAnalyticsSourceState] = useState<{
    tealiumSrc: string;
  }>({
    tealiumSrc: '',
  });

  const { tealiumSrc } = analyticsSourceState;

  // while the old corps-admin-ui repo is being converted over to this repo
  // we need to load specific tealium containers based on the current route
  // so analytics are attributed to the proper GA account. See WDA-27892
  useEffect(() => {
    const isTealiumSrcSet = !!tealiumSrc;
    if (isRouterReady && user && !isTealiumSrcSet) {
      const isGrubhubBrand = user.brand === Brands.GRUBHUB;
      const { isSelfServicePath, isEnterprisePath } = getRouteDetails(pathname);

      if (isSelfServicePath) {
        setAnalyticsSourceState({
          tealiumSrc: selfServiceTealiumSrc,
        });
      } else if (isEnterprisePath) {
        const enterpriseSrc = isGrubhubBrand
          ? enterpriseGrubhubTealiumSrc
          : enterpriseSeamlessTealiumSrc;

        setAnalyticsSourceState({
          tealiumSrc: enterpriseSrc,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRouterReady, user, tealiumSrc, pathname]);

  // wait until we know which tealuim src to use before attempting to load it
  if (!tealiumSrc) {
    return null;
  }

  const isTealiumLoaded = !!get(window, 'utag');
  // if all pertinent scripts are already loaded, don't try to load them again
  if (isTealiumLoaded) {
    return null;
  }

  return (
    <Helmet defer={false}>
      {/* From tealium doc: the Universal Data Object (UDO) is a JavaScript object variable named 'utag_data' in
          which data from your webpage can be passed to Tealium. It is important to determine
          what data you want to capture on each page type. It must be defined and populated prior to loading utag.js.
       */}
      {!isTealiumLoaded && (
        <script type="text/javascript">
          {`
          var utag_data = {
            brand : "", // 
            businesstype : "", // 
            corpclientID : "", // 
            Custom_GAPageTitle : "", // Joined variable with page title + &quot;Umami&quot;
            CustomStitchedURL_GA : "", // combining the dom path and dom hash to pass URL into GA
            eventaction : "", // 
            eventcategory : "", // 
            eventlabel : "", // 
            pagegroup : "", // 
            pagesubgroup : "", // 
            platformtype : "", // 
            userid : "", // 
            corpadmin_creditcardenrollment : "", // Indiciates if firm is enrolled in credit card usage and if so, is a credit card added; possible values are &quot;not enrolled in credit card usage&quot; &quot;credit card usage enabled_added credit card&quot; &quot;credit card usage enabled_no credit card added&quot;
            eventnon_interaction : "", // Used for GA event tracking, possible boolean values of true/false (false is same as a blank value for this parameter)
            grubhubInternalAdmin : "", // 
            corpSalesforceFirmID : "", // Captures the corporate account ID assigned through Salesforce
            corpAdminEmailAddress : "", // 
            churnZeroAppKey : "", // 
            accountExternalId : "", // 
            churnzeroEvent : "", // 
            churnzeroEventDetails : "", // 
            role : "", // “internal_admin” OR “corp_admin” Or “corp_financial_admin” OR “corp_admin_group”
            clientName : "", // 
            corpType : "" // 'PREMIUM' OR 'BASIC'
          };

          (function(a,b,c,d) {
          a='${tealiumSrc}';
          b=document;c='script';d=b.createElement(c);d.src=a;e=window;
          d.type='text/java'+c;d.async=true;d.onload=function(){return e.utagReady&&e.utagReady()};;
          a=b.getElementsByTagName(c)[0];a.parentNode.insertBefore(d,a)})();
        `}
        </script>
      )}
    </Helmet>
  );
};
